import React, { useState, useEffect } from 'react';
import {
  BookOpen,
  X,
  Info,
  CalendarCheck,
  FolderOpen,
  BarChart2,
  Clock,
  ListChecks,
  FileDown,
} from 'lucide-react';
import '../CSS/GuideModal.css';

const AttendanceGuideModal = () => {
  const [isOpen, setIsOpen] = useState(false);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') setIsOpen(false); };
    if (isOpen) document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) setIsOpen(false);
  };

  return (
    <>
      {/* ── Floating Action Button ── */}
      <div className="gml-fab-wrapper">
  <span className="gml-fab-tooltip">Need help?</span>
  <button
    className="gml-fab"
    onClick={() => setIsOpen(true)}
    aria-label="Open Attendance Guide"
    title="Attendance Guide"
  >
    <BookOpen className="gml-fab-icon" />
  </button>
</div>

      {/* ── Modal ── */}
      {isOpen && (
        <div
          className="gml-backdrop"
          onClick={handleBackdropClick}
          role="dialog"
          aria-modal="true"
          aria-labelledby="gml-modal-title"
        >
          <div className="gml-modal">

            {/* Header */}
            <div className="gml-modal-header">
              <div className="gml-modal-title-wrap">
                <BookOpen className="gml-modal-header-icon" />
                <h2 className="gml-modal-title" id="gml-modal-title">
                  Attendance Module Guide
                </h2>
              </div>
              <button
                className="gml-modal-close"
                onClick={() => setIsOpen(false)}
                aria-label="Close guide"
              >
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <div className="gml-modal-body">

              {/* Welcome Banner */}
              <div className="gml-welcome-banner">
                <Info className="gml-welcome-icon" />
                <div>
                  <p className="gml-welcome-heading">
                    Welcome to the Attendance Management Module!
                  </p>
                  <p className="gml-welcome-text">
                    This guide will help you effectively track and manage student
                    attendance records. Use it anytime you need a quick reference.
                  </p>
                </div>
              </div>

              {/* ── Key Features Overview ── */}
              <h3 className="gml-section-heading">Key Features Overview</h3>
              <div className="gml-cards-grid">

                {/* Daily Attendance */}
                <div className="gml-feature-card gml-feature-card--blue">
                  <div className="gml-card-head gml-card-head--blue">
                    <CalendarCheck className="gml-card-head-icon" />
                    <h4 className="gml-card-head-title">Daily Attendance</h4>
                  </div>
                  <div className="gml-card-body">
                    <ul className="gml-card-list">
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--blue">1</span>
                        Record daily attendance for all students
                      </li>
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--blue">2</span>
                        Multiple status options (Present, Absent, Late, Excused)
                      </li>
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--blue">3</span>
                        Track enrollment status changes
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Records Management */}
                <div className="gml-feature-card gml-feature-card--green">
                  <div className="gml-card-head gml-card-head--green">
                    <FolderOpen className="gml-card-head-icon" />
                    <h4 className="gml-card-head-title">Records Management</h4>
                  </div>
                  <div className="gml-card-body">
                    <ul className="gml-card-list">
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--green">1</span>
                        View historical attendance records
                      </li>
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--green">2</span>
                        Sort and filter records by various criteria
                      </li>
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--green">3</span>
                        Bulk delete functionality
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Data Analysis */}
                <div className="gml-feature-card gml-feature-card--yellow">
                  <div className="gml-card-head gml-card-head--yellow">
                    <BarChart2 className="gml-card-head-icon" />
                    <h4 className="gml-card-head-title">Data Analysis</h4>
                  </div>
                  <div className="gml-card-body">
                    <ul className="gml-card-list">
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--yellow">1</span>
                        View individual student attendance statistics
                      </li>
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--yellow">2</span>
                        Export data to multiple formats (Excel, CSV, PDF)
                      </li>
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--yellow">3</span>
                        Visual percentage breakdowns
                      </li>
                    </ul>
                  </div>
                </div>

              </div>

              {/* ── Detailed Feature Guide ── */}
              <h3 className="gml-section-heading">Detailed Feature Guide</h3>
              <div className="gml-detail-section">

                {/* Recording Daily Attendance */}
                <div className="gml-detail-card gml-detail-card--cyan">
                  <div className="gml-detail-head gml-detail-head--cyan">
                    <Clock className="gml-detail-head-icon" />
                    <h5 className="gml-detail-title">Recording Daily Attendance</h5>
                  </div>
                  <div className="gml-detail-body">
                    <p className="gml-detail-sub gml-detail-sub--cyan">Basic Workflow</p>
                    <ul className="gml-detail-list">
                      <li><strong>Select Date:</strong> Choose the date for attendance recording</li>
                      <li><strong>Search / Filter:</strong> Find students by name or class</li>
                      <li><strong>Mark Status:</strong> Select attendance status for each student</li>
                      <li><strong>Submit:</strong> Click "Submit Attendance" to save records</li>
                    </ul>

                    <p className="gml-detail-sub gml-detail-sub--cyan">Enrollment Status</p>
                    <ul className="gml-detail-list">
                      <li><strong>Currently Enrolled:</strong> Default status for active students</li>
                      <li><strong>Passed Out:</strong> For graduated students</li>
                      <li><strong>Expelled:</strong> For students removed from school</li>
                      <li><strong>Withdrawn:</strong> For students who left voluntarily</li>
                    </ul>

                    <p className="gml-detail-sub gml-detail-sub--cyan">Visual Indicators</p>
                    <ul className="gml-detail-list">
                      <li>
                        <span className="gml-inline-badge gml-inline-badge--success">Will be submitted</span>
                        — New changes ready to save
                      </li>
                      <li>
                        <span className="gml-inline-badge gml-inline-badge--muted">No changes</span>
                        — No updates for this student
                      </li>
                      <li>Color-coded enrollment status dropdowns for quick identification</li>
                    </ul>
                  </div>
                </div>

                {/* Managing Records */}
                <div className="gml-detail-card gml-detail-card--yellow">
                  <div className="gml-detail-head gml-detail-head--yellow">
                    <ListChecks className="gml-detail-head-icon" />
                    <h5 className="gml-detail-title">Managing Attendance Records</h5>
                  </div>
                  <div className="gml-detail-body">
                    <p className="gml-detail-sub gml-detail-sub--yellow">Viewing Records</p>
                    <ul className="gml-detail-list">
                      <li>Click "View Records" to see historical data</li>
                      <li>Use search to filter by name, class, or status</li>
                      <li>Sort by date, name, or class in ascending / descending order</li>
                    </ul>

                    <p className="gml-detail-sub gml-detail-sub--yellow">Bulk Operations</p>
                    <ul className="gml-detail-list">
                      <li>Select individual records or use "Select All"</li>
                      <li>Delete multiple records at once</li>
                      <li>A confirmation dialog prevents accidental deletions</li>
                    </ul>

                    <p className="gml-detail-sub gml-detail-sub--yellow">Student Statistics</p>
                    <ul className="gml-detail-list">
                      <li>Hover over student names to see an attendance breakdown</li>
                      <li>View counts and percentages for each status</li>
                      <li>Color-coded for quick visual reference</li>
                    </ul>
                  </div>
                </div>

                {/* Exporting Data */}
                <div className="gml-detail-card gml-detail-card--green">
                  <div className="gml-detail-head gml-detail-head--green">
                    <FileDown className="gml-detail-head-icon" />
                    <h5 className="gml-detail-title">Exporting Data</h5>
                  </div>
                  <div className="gml-detail-body">
                    <p className="gml-detail-sub gml-detail-sub--green">Export Options</p>
                    <ul className="gml-detail-list">
                      <li><strong>Excel:</strong> Fully formatted spreadsheet with all data</li>
                      <li><strong>CSV:</strong> Comma-separated values for data processing</li>
                      <li><strong>PDF:</strong> Print-ready document with proper formatting</li>
                    </ul>

                    <p className="gml-detail-sub gml-detail-sub--green">Included Data</p>
                    <ul className="gml-detail-list">
                      <li>Date of attendance record</li>
                      <li>Student name and class</li>
                      <li>Attendance and enrollment status</li>
                      <li>Registration date</li>
                    </ul>

                    <p className="gml-detail-sub gml-detail-sub--green">Usage Tips</p>
                    <ul className="gml-detail-list">
                      <li>Filter records before exporting to get specific data sets</li>
                      <li>Export regularly for backup purposes</li>
                      <li>Use Excel format for further data analysis</li>
                    </ul>
                  </div>
                </div>

              </div>

              {/* ── Best Practices ── */}
              <div className="gml-best-practices">
                <p className="gml-best-practices-heading">Best Practices</p>
                <ul className="gml-best-practices-list">
                  {[
                    'Record attendance at the same time each day for consistency.',
                    'Review records before submitting to catch any errors.',
                    'Update enrollment status promptly when students leave.',
                    'Export data monthly for archival and backup purposes.',
                    'Use the hover statistics feature to identify attendance patterns early.',
                    'Access this guide anytime via the floating button at the bottom-right of the screen.',
                  ].map((tip, i) => (
                    <li key={i}>
                      <span className="gml-bp-dot" aria-hidden="true" />
                      {tip}
                    </li>
                  ))}
                </ul>
              </div>

            </div>

            {/* Footer */}
            <div className="gml-modal-footer">
              <button className="gml-close-btn" onClick={() => setIsOpen(false)}>
                Got it
              </button>
            </div>

          </div>
        </div>
      )}
    </>
  );
};

export default AttendanceGuideModal;